/**
 * AuthUserListener
 *
 * Listens for SecureStorage changes to the CURRENT_USER entry and keeps the
 * Redux auth slice in sync. Bridges domain writes (e.g.
 * `storeCurrentUserToLocalStorageUseCase`) to global state without forcing
 * domain code to depend on the store.
 *
 * Renders nothing — mount alongside AppInitializer in the app tree.
 *
 * Why `useListenToSecureStorageUpdates` (vs. `eventBus.on` directly):
 * The auth user's source of truth IS the storage value — we read the event
 * payload, remap UserStorage → UserEntity, and dispatch. Going through the
 * storage hook gives us typed access (`<UserStorage>`) and keeps the storage
 * abstraction encapsulated. Compare to WalletListener / PremiumPackagesListener,
 * which discard the payload and re-aggregate via a use case — they use
 * `eventBus.on` directly because the hook's `data` state would be dead weight.
 *
 * Startup race: the hook gates its initial `getEncrypted` read behind
 * `SecureStorageWebCrypto.isInitialized()`, so mounting before storage is
 * initialized is safe. AppInitializer performs the startup dispatch directly;
 * this listener handles every subsequent change.
 */
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/presentation/store';
import { setUser } from '@/presentation/store/slices/auth/authSlice';
import { useListenToSecureStorageUpdates } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { UserStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { UserRemapper } from '@/domain/remappers/user/userRemapper';

export const AuthUserListener = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: userStorage } = useListenToSecureStorageUpdates<UserStorage>(
    SECURE_STORAGE_KEYS.CURRENT_USER,
  );

  useEffect(() => {
    if (!userStorage) return;
    dispatch(setUser(UserRemapper.fromStorage(userStorage)));
  }, [userStorage]);

  return null;
};
